<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="860px">
      <v-card>
        <v-card-title>
          <span>
            {{ dialogTitle }}
          </span>
        </v-card-title>

        <v-card-text @wheel="onWheel">
          <v-form ref="form">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="data.name"
                  :label="
                    $t('wkt.admin.vacation-policy.vacation-policy-window.name')
                  "
                  :rules="[
                    validationRules.required,
                    (value) => validationRules.maxLength(value, 64),
                    duplicateNameRule,
                  ]"
                  :disabled="!canModify"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <span>
                  <strong>{{
                    $t("wkt.admin.vacation-policy.table.days_vacation")
                  }}</strong>
                </span>
              </v-col>
            </v-row>

            <v-row align="center">
              <v-col cols="12" md="5">
                <v-select
                  v-model="data.assistedCountMode"
                  :items="countModeItems"
                  item-text="text"
                  item-value="value"
                  :label="
                    $t(
                      'wkt.admin.vacation-policy.vacation-policy-window.assisted_count_mode',
                    )
                  "
                  :rules="[countModeRule]"
                  :disabled="!canModify"
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model="data.yearlyVacationDays"
                  type="number"
                  min="0"
                  max="365"
                  step="1"
                  :label="
                    $t(
                      'wkt.admin.vacation-policy.vacation-policy-window.yearly_vacation_days',
                    )
                  "
                  :rules="[(value) => optionalRangeIntRule(value, 0, 365)]"
                  :disabled="!canModify"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-btn
                  v-if="canModify"
                  text
                  small
                  color="#185EA9"
                  @click="openResetBalanceDialog(null)"
                >
                  <span>
                    {{
                      $t(
                        "wkt.admin.vacation-policy.tab-general.start_bag_hours_balance",
                      ).toUpperCase()
                    }}
                  </span>
                </v-btn>
              </v-col>
            </v-row>

            <v-row class="mt-n6">
              <v-col cols="12" md="5">
                <span>
                  {{
                    $t(
                      "wkt.admin.vacation-policy.vacation-policy-window.assisted_count_mode_description",
                    )
                  }}
                </span>
              </v-col>
            </v-row>

            <v-row class="mt-2">
              <v-col cols="12">
                <span>
                  <strong>{{
                    $t(
                      "wkt.admin.vacation-policy.additional-policy.dialog.section_bags",
                    )
                  }}</strong>
                </span>
              </v-col>
            </v-row>

            <v-row class="mt-n3">
              <v-col cols="12">
                <span>
                  {{
                    $t(
                      "wkt.admin.vacation-policy.vacation-policy-window.section_bag_hours_description",
                    )
                  }}
                </span>
              </v-col>
            </v-row>

            <v-row v-if="enabledBagDefinitions.length" class="mt-n2">
              <v-col cols="12" md="6" />
              <v-col cols="12" md="2">
                <span>
                  <strong>{{
                    $t("wkt.admin.vacation-policy.bag-config.unit")
                  }}</strong>
                </span>
              </v-col>
              <v-col cols="12" md="4" />
            </v-row>

            <v-row
              v-for="definition in enabledBagDefinitions"
              :key="definition.id"
              align="center"
              class="mt-n4"
            >
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="data[definition.policyProp]"
                  type="number"
                  min="0"
                  step="1"
                  :label="definition.displayLabel"
                  :rules="[(value) => bagAnnualValueRule(definition, value)]"
                  :disabled="!canModify"
                />
              </v-col>

              <v-col cols="12" md="2">
                <span>
                  {{ getUnitText(definition) }}
                </span>
              </v-col>

              <v-col cols="12" md="4">
                <v-btn
                  v-if="canModify"
                  text
                  small
                  color="#185EA9"
                  @click="openResetBalanceDialog(definition)"
                >
                  <span>
                    {{
                      $t(
                        "wkt.admin.vacation-policy.tab-general.start_bag_hours_balance",
                      ).toUpperCase()
                    }}
                  </span>
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-btn
            v-if="canModify && data.id"
            color="error"
            dark
            @click="confirmDelete"
          >
            <v-icon left>delete</v-icon>
            <span>{{
              $t("wkt.admin.common.dialog-buttons.delete").toUpperCase()
            }}</span>
          </v-btn>
          <v-spacer />
          <v-btn v-if="canModify" color="#185EA9" text @click="ok">
            {{ $t("wkt.admin.common.dialog-buttons.ok").toUpperCase() }}
          </v-btn>
          <v-btn text @click="showDialog = false">
            {{ $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase() }}
          </v-btn>
        </v-card-actions>

        <LoadingDialog :loading="loading" />
      </v-card>
    </v-dialog>

    <ConfirmDialog
      ref="deleteConfirmDialog"
      :title="
        $t('wkt.admin.vacation-policy.additional-policy.delete_confirm.title')
      "
      :message="
        $t('wkt.admin.vacation-policy.additional-policy.delete_confirm.message')
      "
      :title-ok="$t('wkt.admin.common.dialog-buttons.delete')"
      :title-cancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      @confirmed="deletePolicy"
    />

    <VacationPolicyBalanceResetDialog ref="resetDialog" />
  </v-row>
</template>

<script>
import api from "../../../api/worktime";
import BaseModule from "../../common/BaseModule";
import ConfirmDialog from "../../common/ConfirmDialog";
import LoadingDialog from "../../common/LoadingDialog";
import deepClone from "../../util/deep-clone";
import VacationPolicyBalanceResetDialog from "./VacationPolicyBalanceResetDialog";

const HOURS_BAG_UNIT_DAYS = 0;
const HOURS_BAG_UNIT_HOURS = 1;

export default {
  name: "VacationPolicyEditorDialog",
  mixins: [BaseModule],
  components: {
    ConfirmDialog,
    LoadingDialog,
    VacationPolicyBalanceResetDialog,
  },
  data() {
    return {
      showDialog: false,
      loading: false,
      canModify: false,
      company: null,
      callback: null,
      existingPolicies: [],
      data: this.buildEmptyPolicy(null),
    };
  },
  computed: {
    dialogTitle() {
      return this.data && this.data.id
        ? this.$t(
            "wkt.admin.vacation-policy.additional-policy.dialog.title.edit",
          )
        : this.$t(
            "wkt.admin.vacation-policy.additional-policy.dialog.title.new",
          );
    },
    countModeItems() {
      return [
        {
          value: 0,
          text: this.$t(
            "wkt.admin.vacation-policy.vacation-policy-window.count_mode_none",
          ),
        },
        {
          value: 1,
          text: this.$t(
            "wkt.admin.vacation-policy.vacation-policy-window.count_mode_natural_days",
          ),
        },
        {
          value: 2,
          text: this.$t(
            "wkt.admin.vacation-policy.vacation-policy-window.count_mode_none_work_days",
          ),
        },
      ];
    },
    enabledBagDefinitions() {
      if (!this.company) {
        return [];
      }

      return [
        {
          id: 7,
          displayLabel:
            this.company.defaultYearlyExtraLabel ||
            this.$t("wkt.admin.vacation-policy.table.days_extra"),
          policyProp: "yearlyExtraDays",
          unitProp: "defaultYearlyExtraUnit",
        },
        {
          id: 1,
          displayLabel:
            this.company.defaultYearlyHoursBag1Label ||
            this.$t("wkt.admin.vacation-policy.table.bag1"),
          policyProp: "yearlyHoursBag1Days",
          unitProp: "defaultYearlyHoursBag1Unit",
        },
        {
          id: 2,
          displayLabel:
            this.company.defaultYearlyHoursBag2Label ||
            this.$t("wkt.admin.vacation-policy.table.bag2"),
          policyProp: "yearlyHoursBag2Days",
          unitProp: "defaultYearlyHoursBag2Unit",
        },
        {
          id: 3,
          displayLabel:
            this.company.defaultYearlyHoursBag3Label ||
            this.$t("wkt.admin.vacation-policy.table.bag3"),
          policyProp: "yearlyHoursBag3Days",
          unitProp: "defaultYearlyHoursBag3Unit",
        },
        {
          id: 4,
          displayLabel:
            this.company.defaultYearlyHoursBag4Label ||
            this.$t("wkt.admin.vacation-policy.table.bag4"),
          policyProp: "yearlyHoursBag4Days",
          unitProp: "defaultYearlyHoursBag4Unit",
        },
        {
          id: 5,
          displayLabel:
            this.company.defaultYearlyHoursBag5Label ||
            this.$t("wkt.admin.vacation-policy.table.bag5"),
          policyProp: "yearlyHoursBag5Days",
          unitProp: "defaultYearlyHoursBag5Unit",
        },
      ].filter((definition) => {
        if (definition.id === 7) {
          return Boolean(this.company.defaultYearlyExtraEnabled);
        }

        return Boolean(
          this.company[`defaultYearlyHoursBag${definition.id}Enabled`],
        );
      });
    },
  },
  methods: {
    emitError(message) {
      if (this.callback) {
        this.callback({
          success: false,
          message,
        });
      }
    },
    emitSuccess(message) {
      if (this.callback) {
        this.callback({
          success: true,
          message:
            message ||
            this.$t("wkt.admin.general-customize.data_saved_correctly"),
        });
      }
    },
    onWheel(event) {
      event.stopPropagation();
    },
    buildEmptyPolicy(company) {
      return {
        id: null,
        companyId: company ? company.id : null,
        name: "",
        assistedCountMode: 0,
        yearlyVacationDays: 0,
        yearlyExtraDays: 0,
        yearlyHoursBag1Days: 0,
        yearlyHoursBag2Days: 0,
        yearlyHoursBag3Days: 0,
        yearlyHoursBag4Days: 0,
        yearlyHoursBag5Days: 0,
      };
    },
    open(company, policy, existingPolicies, canModify, callback) {
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      this.company = deepClone(company);
      this.existingPolicies = Array.isArray(existingPolicies)
        ? existingPolicies
        : [];
      this.canModify = canModify;
      this.callback = callback;
      this.data = policy
        ? deepClone(policy)
        : this.buildEmptyPolicy(this.company);

      if (this.data.companyId == null && this.company) {
        this.data.companyId = this.company.id;
      }

      this.showDialog = true;
    },
    duplicateNameRule(value) {
      const normalizedValue = (value || "").trim().toLowerCase();

      if (!normalizedValue) {
        return true;
      }

      const duplicated = this.existingPolicies.some((policy) => {
        return (
          policy.id !== this.data.id &&
          (policy.name || "").trim().toLowerCase() === normalizedValue
        );
      });

      return (
        !duplicated ||
        this.$t(
          "wkt.admin.vacation-policy.vacation-policy-window.duplicate.error",
        )
      );
    },
    countModeRule(value) {
      return value !== null && value !== undefined
        ? true
        : this.$t("wkt.admin.common.validation-utils.required_field");
    },
    optionalRangeIntRule(value, minimum, maximum) {
      if (value === null || value === undefined || value === "") {
        return true;
      }

      const numericValue = Number(value);

      if (Number.isNaN(numericValue) || !Number.isInteger(numericValue)) {
        return this.$t(
          "wkt.admin.vacation-policy.validation.integer_annual_value",
        );
      }

      return (
        (numericValue >= minimum && numericValue <= maximum) ||
        `${this.$t(
          "wkt.admin.common.validation-utils.required_field",
        )} [ ${minimum} - ${maximum} ]`
      );
    },
    getUnit(definition) {
      if (!this.company || !definition || !definition.unitProp) {
        return HOURS_BAG_UNIT_DAYS;
      }

      return Number(this.company[definition.unitProp] || HOURS_BAG_UNIT_DAYS);
    },
    getUnitText(definition) {
      return this.getUnit(definition) === HOURS_BAG_UNIT_HOURS
        ? this.$t("wkt.admin.vacation-policy.unit.hours")
        : this.$t("wkt.admin.vacation-policy.unit.days");
    },
    bagAnnualValueRule(definition, value) {
      const maximum =
        this.getUnit(definition) === HOURS_BAG_UNIT_HOURS ? 8760 : 365;

      return this.optionalRangeIntRule(value, 0, maximum);
    },
    normalizeIntegerValue(value) {
      const numericValue = Number(value);
      return Number.isNaN(numericValue) ? 0 : Math.trunc(numericValue);
    },
    normalizeNumericFields() {
      this.data.companyId = this.company
        ? this.company.id
        : this.data.companyId;
      this.data.assistedCountMode = Number(this.data.assistedCountMode || 0);
      this.data.yearlyVacationDays = this.normalizeIntegerValue(
        this.data.yearlyVacationDays,
      );
      this.data.yearlyExtraDays = this.normalizeIntegerValue(
        this.data.yearlyExtraDays,
      );
      this.data.yearlyHoursBag1Days = this.normalizeIntegerValue(
        this.data.yearlyHoursBag1Days,
      );
      this.data.yearlyHoursBag2Days = this.normalizeIntegerValue(
        this.data.yearlyHoursBag2Days,
      );
      this.data.yearlyHoursBag3Days = this.normalizeIntegerValue(
        this.data.yearlyHoursBag3Days,
      );
      this.data.yearlyHoursBag4Days = this.normalizeIntegerValue(
        this.data.yearlyHoursBag4Days,
      );
      this.data.yearlyHoursBag5Days = this.normalizeIntegerValue(
        this.data.yearlyHoursBag5Days,
      );
    },
    openResetBalanceDialog(definition) {
      if (!this.$refs.form.validate()) {
        return;
      }

      const isVacationBag = !definition;
      const bagLabel = isVacationBag
        ? this.$t("wkt.admin.vacation-policy.table.days_vacation")
        : definition.displayLabel;

      this.$refs.resetDialog.open({ bagLabel }, (resetOptions) => {
        this.save(resetOptions, definition);
      });
    },
    ok() {
      this.save();
    },
    save(resetOptions = null, definition = null) {
      if (!this.canModify) {
        this.showDialog = false;
        return;
      }

      if (!this.$refs.form.validate()) {
        return;
      }

      this.loading = true;
      this.normalizeNumericFields();
      const policyPayload = deepClone(this.data);

      api.storeVacationPolicy(
        policyPayload,
        (response) => {
          if (!response || response.rsOK === false) {
            this.loading = false;
            this.emitError(
              response
                ? response.message
                : this.$t("wkt.admin.common.error.save_server"),
            );
            return;
          }

          if (resetOptions) {
            this.handleResetAfterStore(
              policyPayload,
              resetOptions,
              definition,
              response.message,
            );
            return;
          }

          this.loading = false;
          this.showDialog = false;
          this.data = deepClone(policyPayload);
          this.emitSuccess(response.message);
        },
        (error) => {
          this.loading = false;
          this.emitError(this.getCallbackErrorMessage(
            error,
            this.$t("wkt.admin.common.error.save_server"),
          ));
        },
      );
    },
    handleResetAfterStore(
      policyPayload,
      resetOptions,
      definition,
      baseMessage,
    ) {
      if (policyPayload.id != null) {
        this.data = deepClone(policyPayload);
        this.executeUpdateBalance(resetOptions, definition, baseMessage);
        return;
      }

      this.fetchStoredPolicyByName(policyPayload.name, (storedPolicy) => {
        if (!storedPolicy || storedPolicy.id == null) {
          this.loading = false;
          this.emitError(this.$t("wkt.admin.common.error.save_server"));
          return;
        }

        this.data = deepClone(storedPolicy);
        this.executeUpdateBalance(resetOptions, definition, baseMessage);
      });
    },
    fetchStoredPolicyByName(policyName, callback) {
      const normalizedName = (policyName || "").trim().toLowerCase();

      api.getVacationPolicies(
        policyName,
        (policies) => {
          const storedPolicy = (Array.isArray(policies) ? policies : []).find(
            (policy) => {
              return (
                policy.companyId === this.data.companyId &&
                (policy.name || "").trim().toLowerCase() === normalizedName
              );
            },
          );

          callback(storedPolicy || null);
        },
        (error) => {
          this.loading = false;
          this.emitError(this.getCallbackErrorMessage(
            error,
            this.$t("wkt.admin.common.error.save_server"),
          ));
        },
      );
    },
    executeUpdateBalance(resetOptions, definition, baseMessage) {
      const filters = api.getCurrentAdminFilters(this.company.id);
      const hoursBagId = definition ? definition.id : 6;
      const hoursBagDays = definition
        ? Number(this.data[definition.policyProp] || 0)
        : Number(this.data.yearlyVacationDays || 0);

      api.updateBalanceVacation(
        {
          workplaceId: filters.workplaceId,
          departmentId: filters.departmentId,
          vacationPolicyId: this.data.id,
          hoursBagId,
          hoursBagDays,
          accumulateVacation: Boolean(resetOptions.accumulateVacation),
        },
        (response) => {
          this.loading = false;

          if (!response || !response.rsOK) {
            this.emitError(
              response
                ? response.message
                : this.$t("wkt.admin.common.error.save_server"),
            );
            return;
          }

          if (this.callback) {
            this.callback({
              success: true,
              message:
                response.message ||
                baseMessage ||
                this.$t("wkt.admin.general-customize.data_saved_correctly"),
            });
          }
        },
        (error) => {
          this.loading = false;
          this.emitError(this.getCallbackErrorMessage(
            error,
            this.$t("wkt.admin.common.error.save_server"),
          ));
        },
      );
    },
    confirmDelete() {
      this.$refs.deleteConfirmDialog.confirm(this.data.id);
    },
    deletePolicy() {
      this.loading = true;

      api.deleteVacationPolicy(
        this.data.id,
        (response) => {
          this.loading = false;

          if (response && response.rsOK === false) {
            this.emitError(
              response.message || this.$t("wkt.admin.common.error.save_server"),
            );
            return;
          }

          this.showDialog = false;

          if (this.callback) {
            this.callback({
              success: true,
              message:
                response?.message ||
                this.$t("wkt.admin.general-customize.data_saved_correctly"),
            });
          }
        },
        (error) => {
          this.loading = false;
          this.emitError(this.getCallbackErrorMessage(
            error,
            this.$t("wkt.admin.common.error.save_server"),
          ));
        },
      );
    },
  },
};
</script>
